import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, X } from "lucide-react";

/**
 * PickerWithAdd — a dropdown with an inline "+ Add new" button.
 *
 * props:
 *   value           - current selected value (string)
 *   onChange(val, item?) - called when selection changes
 *   options         - array of {value, label, meta?} — meta is passed back for prefill
 *   allowFreeText   - if true, allow typing a new value manually (default false)
 *   placeholder     - dropdown placeholder
 *   testid          - data-testid prefix
 *   addLabel        - text for the add button (default "New")
 *   onCreate(text)  - async function that creates a new master record and returns the new option (or null)
 */
export default function PickerWithAdd({
  value, onChange, options = [], placeholder = "Select…",
  testid = "picker", addLabel = "New", onCreate,
  requirePhone = false,
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim() || !onCreate) return;
    if (requirePhone && !phone.trim()) {
      toast.error("Phone number is required to avoid duplicates");
      return;
    }
    setBusy(true);
    try {
      const newOpt = await onCreate(text.trim(), phone.trim());
      if (newOpt) {
        onChange(newOpt.value, newOpt.meta);
        toast.success(`Added: ${newOpt.label}`);
      }
      setText(""); setPhone(""); setAdding(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
    setBusy(false);
  };

  if (adding) {
    return (
      <div className="flex flex-col gap-1" data-testid={`${testid}-add-form`}>
        <div className="flex gap-1">
          <input
            autoFocus className="input-field flex-1" placeholder={`New ${addLabel.toLowerCase()} name`}
            value={text} onChange={e=>setText(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter" && !requirePhone){e.preventDefault();submit();} if(e.key==="Escape"){setAdding(false);setText("");setPhone("");} }}
            data-testid={`${testid}-add-input`}
          />
          {!requirePhone && <button type="button" onClick={submit} disabled={busy} className="btn-primary text-xs px-2" data-testid={`${testid}-add-save`}><Check size={14}/></button>}
          {!requirePhone && <button type="button" onClick={()=>{setAdding(false);setText("");setPhone("");}} className="btn-ghost text-xs px-2"><X size={14}/></button>}
        </div>
        {requirePhone && (
          <div className="flex gap-1">
            <input
              className="input-field flex-1" placeholder="Phone number (required)"
              value={phone} onChange={e=>setPhone(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter"){e.preventDefault();submit();} if(e.key==="Escape"){setAdding(false);setText("");setPhone("");} }}
              data-testid={`${testid}-add-phone`}
            />
            <button type="button" onClick={submit} disabled={busy || !phone.trim() || !text.trim()} className="btn-primary text-xs px-2 disabled:opacity-50" data-testid={`${testid}-add-save`}><Check size={14}/></button>
            <button type="button" onClick={()=>{setAdding(false);setText("");setPhone("");}} className="btn-ghost text-xs px-2"><X size={14}/></button>
          </div>
        )}
      </div>
    );
  }

  const hasValue = value && !options.find(o => o.value === value);

  return (
    <div className="flex gap-1">
      <select
        className="input-field flex-1"
        value={value || ""}
        data-testid={testid}
        onChange={e => {
          const opt = options.find(o => o.value === e.target.value);
          onChange(e.target.value, opt?.meta);
        }}
      >
        <option value="">{placeholder}</option>
        {hasValue && <option value={value}>{value} (current)</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {onCreate && (
        <button type="button" onClick={()=>setAdding(true)}
          className="btn-ghost text-xs px-2 whitespace-nowrap flex items-center gap-1"
          data-testid={`${testid}-add-btn`} title={`Add new ${addLabel.toLowerCase()}`}>
          <Plus size={12}/> New
        </button>
      )}
    </div>
  );
}

// Ready-made hooks/creators for common masters -----------------------------

export async function createCustomer(name, phone = "") {
  const { data } = await api.post("/customers", { name, phone });
  return { value: data.id, label: data.name, meta: data };
}
export async function createVendor(name, phone = "") {
  const { data } = await api.post("/vendors", { name, phone });
  // Vendor pickers across the app use vendor NAME as the value (not id) — keep that consistent.
  return { value: data.name, label: data.name, meta: data };
}
export async function createProduct(name) {
  const { data } = await api.post("/products", { name, default_price: 0 });
  return { value: data.name, label: data.name, meta: data };
}
